import React, { useEffect } from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  InputNumber,
  message,
  Select,
} from 'antd';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { TurnsService } from 'services/TurnsService';
import { requiredRule } from 'utils/forms/validationRules';
import { formatDate } from 'utils';
import { thousandFormatter } from 'utils/forms/formatter';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import dayjs from 'dayjs';

export const turnsType = [
  { value: 'cash', text: 'Наличные' },
  { value: 'cashless', text: 'Безналичные' },
  { value: 'accruals', text: 'Начисления' },
];

function TurnsForm({ initialValues, clientId, isActive, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;

  useEffect(() => {
    if (initialValues) {
      const { type, period, doc_id, sum } = initialValues;

      form.setFieldsValue({
        type,
        period: dayjs(period),
        doc_id,
        sum,
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const handleSubmit = (values) => {
    const payload = {
      ...values,
      id: initialValues?.id,
      period: formatDate(values?.period, 'YYYY-MM-DD'),
      client_id: clientId,
    };

    if (isEditing) {
      delete payload.client_id;
      handleUpdate(payload, initialValues.id);
    } else {
      delete payload.id;
      handleCreate(payload);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await TurnsService.createTurns(data);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await TurnsService.updateTurns(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onSuccess();
    onClose();
    form.resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование оборота' : 'Создание оборота'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
      >
        <Form.Item name="type" label="Тип" rules={[requiredRule]}>
          <Select placeholder="Выберите тип" allowClear>
            {turnsType.map(({ text, value }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="period" label="Период" rules={[requiredRule]}>
          <DatePicker
            format="DD.MM.YYYY"
            style={{ width: '100%' }}
            placeholder="Введите период"
          />
        </Form.Item>

        <Form.Item name="doc_id" label="Номер документа" rules={[requiredRule]}>
          <InputNumber
            addonAfter="№"
            controls={false}
            placeholder="Введите номер документа"
            style={{ width: '100%' }}
            readOnly={isEditing}
          />
        </Form.Item>

        <Form.Item name="sum" label="Сумма" rules={[requiredRule]}>
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Введите сумму"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { TurnsForm };
